<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Product + Cost + Shipping Fee"
                sample-csv-path="/importProductWithCostAndEstimatedShippingFeeSample.csv"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Import Product + Product Cost + Estimated Shipping Fee </v-col>
                        <v-col cols="12" sm="12" md="6"> </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <p>
                        <b>Important:</b> Maximum {{ maxRowLimit }} products per import. For larger imports, please
                        split your CSV file into multiple batches.
                    </p>
                    <p>
                        <b>Note:</b> The Effective Date for Product Cost and Estimated Shipping Fee will be
                        automatically set to the current date (today).
                    </p>
                    <p>
                        <master-data-reference-button
                            button-label="Category List"
                            dialog-title="Valid Category Codes"
                            :items="categoryOptions"
                        />
                    </p>

                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" :to="pathListProduct"> Back to list </v-btn>
                            <span v-if="loading && totalRows > 0" class="ml-2">
                                Processing {{ currentProgress }}/{{ totalRows }} ({{
                                    Math.round((currentProgress / totalRows) * 100)
                                }}%)
                            </span>
                            <span v-if="importDone">{{ importDoneMsg }} </span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td v-for="header in data.headers" :key="header.value" style="white-space: pre-line">
                                    {{ item[header.value] }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import { ValidationHelper } from '../../utils/ValidationHelper';
import { Formatter } from '../../utils/Formatter';
import { MasterDataApiCaller } from '../../utils/MasterDataApiCaller';
import { ProductHeaderHelper } from '../../utils/headerHelper/ProductHeaderHelper';
import { ProductCostHeaderHelper } from '../../utils/headerHelper/ProductCostHeaderHelper';
import { EstimatedShippingFeeHeaderHelper } from '../../utils/headerHelper/EstimatedShippingFeeHeaderHelper';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import { useMasterData } from '../../composables/useMasterData';
import MasterDataReferenceButton from '../../components/MasterDataReferenceButton.vue';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { ProductImportValidation } from '../../config/importValidationConfigs';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';

export default defineComponent({
    name: 'ImportProduct',

    components: {
        MasterDataReferenceButton,
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        // Load master data options for dropdowns
        const { categoryOptions, loadAllMasterData } = useMasterData();

        const data = reactive({
            mappedCsv: null,
            fields: {
                ...ProductHeaderHelper.getImportFields(),
                ...ProductCostHeaderHelper.getImportWithCostFields(),
                ...EstimatedShippingFeeHeaderHelper.getImportWithShippingFeeFields()
            },
            previewHeaders: [
                ...ProductHeaderHelper.getImportHeaders(),
                ...ProductCostHeaderHelper.getImportWithCostHeaders(),
                ...EstimatedShippingFeeHeaderHelper.getImportWithShippingFeeHeaders()
            ],
            headers: [
                ...ImportHeaderHelper.getHeaders(),
                ...ProductHeaderHelper.getImportHeaders(),
                ...ProductCostHeaderHelper.getImportWithCostHeaders(),
                ...EstimatedShippingFeeHeaderHelper.getImportWithShippingFeeHeaders()
            ]
        });

        onMounted(async () => {
            // Load master data for reference button
            await loadAllMasterData();
        });

        return {
            data,
            categoryOptions
        };
    },
    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListProduct: PathDefinition.listProduct,
            maxRowLimit: 200,
            currentProgress: 0,
            totalRows: 0
        };
    },
    async created() {},
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            const importHeaderValues = this.data.headers.map((header) => header.value);
            return ImportValidationHelper.validateRows(mappedCsv, ProductImportValidation, importHeaderValues);
        },
        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                // Check row limit
                if (this.data.mappedCsv.length > this.maxRowLimit) {
                    alert(
                        `Too many rows. Maximum ${this.maxRowLimit} products allowed per import.\n\nYour file has ${this.data.mappedCsv.length} rows.\n\nPlease split your CSV file into smaller batches.`
                    );
                    return;
                }
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },
        async importSubmit() {
            this.loading = true;
            this.currentProgress = 0;
            this.totalRows = this.data.mappedCsv.length;

            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                for (let i = 0; i < this.data.mappedCsv.length; i++) {
                    await this.create(i);
                    this.currentProgress = i + 1;
                }
                this.importDone = true;
                // prevent import again
                this.invalidFile = true;
            } else {
                console.log('invalid csv');
            }
            this.loading = false;
            this.currentProgress = 0;
            this.totalRows = 0;
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            return '';
        },
        isInvalidRow(item) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },
        isInvalidRowContent(item) {
            return ImportValidationHelper.hasValidationErrors(item, ProductImportValidation);
        },
        async create(idx) {
            const csvData = this.data.mappedCsv[idx];
            const results = [];

            // Step 1: Create Product
            const productResponse = await this.createProduct(csvData);
            if (!productResponse.success) {
                if (this.isProductAlreadyExistsError(productResponse)) {
                    results.push('Product: Already exists');
                    // Continue to create/update cost and shipping fee
                } else {
                    results.push(`Product: Failed - ${this.extractErrorMessage(productResponse)}`);
                    results.push('Cost: Not attempted');
                    results.push('Shipping: Not attempted');
                    csvData.result = results.join('\n');
                    csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                    csvData.error = this.extractErrorMessage(productResponse);
                    this.data.mappedCsv.splice(idx, 1, csvData);
                    return;
                }
            } else {
                results.push('Product: Created');
            }

            // Step 2: Create Product Cost
            const productCostResponse = await this.createProductCost(csvData);
            if (!productCostResponse.success) {
                results.push(`Cost: Failed - ${this.extractErrorMessage(productCostResponse)}`);
                results.push('Shipping: Not attempted');
                csvData.result = results.join('\n');
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                csvData.error = this.extractErrorMessage(productCostResponse);
                this.data.mappedCsv.splice(idx, 1, csvData);
                return;
            } else {
                results.push('Cost: Success');
            }

            // Step 3: Create Estimated Shipping Fee
            const estimatedShippingFeeResponse = await this.createEstimatedShippingFee(csvData);
            if (!estimatedShippingFeeResponse.success) {
                results.push(`Shipping: Failed - ${this.extractErrorMessage(estimatedShippingFeeResponse)}`);
                csvData.result = results.join('\n');
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                csvData.error = this.extractErrorMessage(estimatedShippingFeeResponse);
            } else {
                results.push('Shipping: Success');
                csvData.result = results.join('\n');
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.success;
            }

            this.data.mappedCsv.splice(idx, 1, csvData);
        },
        async createProduct(csvData) {
            const productRequestFields = ProductHeaderHelper.getImportHeaders().map((header) => header.value);
            const request = _.pick(csvData, productRequestFields);
            const requestUrl = `/api/staff/product/create`;
            return await fetchWrapper.post(requestUrl, request);
        },
        async createProductCost(csvData) {
            const productCostRequestFields = ['sku', 'productCostAmount'];
            const request = {
                ..._.pick(csvData, productCostRequestFields),
                dateEffectiveOn: Formatter.convertDateToStandardFormatDateStr(new Date())
            };
            const requestUrl = `/api/staff/productCost/create`;
            return await fetchWrapper.post(requestUrl, request);
        },
        async createEstimatedShippingFee(csvData) {
            const estimatedShippingFeeRequestFields = ['sku', 'estimatedShippingFeeAmount'];
            const request = {
                ..._.pick(csvData, estimatedShippingFeeRequestFields),
                dateEffectiveOn: Formatter.convertDateToStandardFormatDateStr(new Date())
            };
            const requestUrl = `/api/staff/estimatedShippingFee/create`;
            return await fetchWrapper.post(requestUrl, request);
        },
        isProductAlreadyExistsError(response) {
            const errorMsg = response.errors || '';
            return (
                (errorMsg.includes('CONFLICT') && errorMsg.includes('already exists')) ||
                errorMsg.includes('Product with this SKU already exists')
            );
        },
        extractErrorMessage(response) {
            const errors = response.errors || '';
            // Handle different error formats
            if (typeof errors === 'object') {
                return JSON.stringify(errors);
            }
            // Extract just the message part if it's formatted as "CODE: Message"
            if (typeof errors === 'string' && errors.includes(':')) {
                return errors.split(':').slice(1).join(':').trim();
            }
            return String(errors);
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}
</style>
